import React, { useEffect, useCallback, useRef } from "react";
import mermaid from "mermaid";

interface ClaimTreeSelectorProps {
  structure: string;
  claimKeys: string[];
  selectedClaims: string[];
  onToggleClaim: (claimKey: string) => void;
}

const SELECTED_FILL = "#1751D0";
const SELECTED_STROKE = "#1751D0";
const SELECTED_TEXT = "#FFFFFF";
const DESELECTED_FILL = "#FFFFFF";
const DESELECTED_STROKE = "#CFD6DD";
const DESELECTED_TEXT = "#272E35";

const ClaimTreeSelector: React.FC<ClaimTreeSelectorProps> = ({
  structure,
  claimKeys,
  selectedClaims,
  onToggleClaim,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const renderIdRef = useRef(`claim-tree-${Math.random().toString(36).slice(2)}`);
  const onToggleRef = useRef(onToggleClaim);
  onToggleRef.current = onToggleClaim;

  // Extract claim number from SVG node text content like "Claim 1: ..."
  const extractClaimKey = (textContent: string): string | null => {
    const match = textContent.match(/Claim\s+(\d+)/i);
    if (match) {
      return `claim${match[1]}`;
    }
    return null;
  };

  const applyNodeStyles = useCallback(() => {
    if (!containerRef.current) return;

    const nodes = containerRef.current.querySelectorAll(".node");
    nodes.forEach((node) => {
      const textEl = node.querySelector(".nodeLabel, foreignObject, text");
      const textContent = textEl?.textContent || node.textContent || "";
      const claimKey = extractClaimKey(textContent);
      if (!claimKey || !claimKeys.includes(claimKey)) return;

      const isSelected = selectedClaims.includes(claimKey);
      const rect = node.querySelector("rect, .basic, polygon, circle, ellipse, path");

      if (rect) {
        (rect as SVGElement).style.fill = isSelected ? SELECTED_FILL : DESELECTED_FILL;
        (rect as SVGElement).style.stroke = isSelected ? SELECTED_STROKE : DESELECTED_STROKE;
      }

      // Style all text elements inside the node
      const texts = node.querySelectorAll("text, tspan, span, p, div");
      texts.forEach((t) => {
        (t as HTMLElement).style.color = isSelected ? SELECTED_TEXT : DESELECTED_TEXT;
        (t as SVGElement).style.fill = isSelected ? SELECTED_TEXT : DESELECTED_TEXT;
      });
    });
  }, [selectedClaims, claimKeys]);

  const renderDiagram = useCallback(async () => {
    if (!containerRef.current || !structure) return;

    try {
      containerRef.current.innerHTML = "";

      await mermaid.initialize({
        startOnLoad: false,
        securityLevel: "loose",
        theme: "base",
        themeVariables: {
          background: "#FFFFFF",
          primaryColor: "#fff",
          primaryBorderColor: "#CFD6DD",
          primaryBorderWidth: "2px",
          nodeBorder: "#CFD6DD",
          nodeBorderWidth: "2px",
          nodeTextColor: "#272E35",
          fontFamily: "Inter",
          fontSize: "12px",
          borderRadius: "8px",
          nodeRadius: "8px",
        },
      });

      await mermaid.parse(structure);
      const { svg } = await mermaid.render(renderIdRef.current, structure);

      if (containerRef.current) {
        containerRef.current.innerHTML = svg;

        // Make nodes clickable
        const nodes = containerRef.current.querySelectorAll(".node");
        nodes.forEach((node) => {
          const textEl = node.querySelector(".nodeLabel, foreignObject, text");
          const textContent = textEl?.textContent || node.textContent || "";
          const claimKey = extractClaimKey(textContent);
          if (!claimKey || !claimKeys.includes(claimKey)) return;

          (node as HTMLElement).style.cursor = "pointer";
          node.addEventListener("click", (e) => {
            e.stopPropagation();
            onToggleRef.current(claimKey);
          });
        });

        applyNodeStyles();
      }
    } catch (error) {
      console.error("Failed to render claim tree:", error);
      if (containerRef.current) {
        containerRef.current.innerHTML = `
          <div class="flex items-center justify-center p-4 text-center">
            <span class="text-xs text-[#7F7D83]">Could not render claim structure</span>
          </div>
        `;
      }
    }
  }, [structure, claimKeys]);

  // Render diagram once when structure is available
  useEffect(() => {
    const timer = setTimeout(renderDiagram, 100);
    return () => clearTimeout(timer);
  }, [renderDiagram]);

  // Re-apply styles when selection changes (without re-rendering the whole diagram)
  useEffect(() => {
    applyNodeStyles();
  }, [applyNodeStyles]);

  return (
    <div
      ref={containerRef}
      className="claim-tree-selector w-full overflow-auto"
      style={{ maxHeight: "380px" }}
    />
  );
};

export default ClaimTreeSelector;
